import React, { useState, useMemo } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

/**
 * Expects `results` to be an object like:
 * {
 *   Realistic: 0.25,
 *   Investigative: 0.1,
 *   Artistic: 0.5,
 *   Social: 0.35,
 *   Enterprising: 0.15,
 *   Conventional: 0.2
 * }
 * Each value typically between 0 and 1 (normalized).
 */

// Short descriptions for each category
const descriptions = {
  "Realistic (R)":
    "Practical, hands-on, physically active; enjoys building, fixing, or working outdoors.",
  "Investigative (I)":
    "Analytical, intellectual, scientific; enjoys research, problem-solving, and understanding how things work.",
  "Artistic (A)":
    "Creative, original, expressive; enjoys art, design, music, writing, and freedom of expression.",
  "Social (S)":
    "Empathetic, collaborative, supportive; enjoys helping others, teaching, and community involvement.",
  "Enterprising (E)":
    "Persuasive, ambitious, risk-taking; enjoys leadership, business ventures, and motivating others.",
  "Conventional (C)":
    "Organized, detail-oriented, structured; enjoys data management, routines, and systematic work.",
};

/** 
 * Custom tick component for clickable category labels.
 * Clicking sets `selectedCategory` to the label text.
 */
function CustomPolarAngleAxisTick({ payload, x, y, setSelectedCategory }) {
  return (
    <text
      x={x}
      y={y}
      fill="#444"
      textAnchor="middle"
      dominantBaseline="central"
      onClick={() => setSelectedCategory(payload.value)}
      style={{ cursor: "pointer", fontSize: 12, userSelect: "none" }}
    >
      {payload.value}
    </text>
  );
}

const RIASECHexagonChart = ({ results }) => {
  // State for whichever category label the user last clicked
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Convert normalized [0..1] to [0..100]
  const finalData = useMemo(
    () => [
      { category: "Realistic (R)", score: Math.round((results?.Realistic || 0) * 100) },
      { category: "Investigative (I)", score: Math.round((results?.Investigative || 0) * 100) },
      { category: "Artistic (A)", score: Math.round((results?.Artistic || 0) * 100) },
      { category: "Social (S)", score: Math.round((results?.Social || 0) * 100) },
      { category: "Enterprising (E)", score: Math.round((results?.Enterprising || 0) * 100) },
      { category: "Conventional (C)", score: Math.round((results?.Conventional || 0) * 100) },
    ],
    [results]
  );

  // Sort a copy to identify relative ranks
  const sorted = [...finalData].sort((a, b) => b.score - a.score); // highest to lowest

  // Helper function to build a message about the rank / meaning
  const getRelativeRankInfo = (catLabel) => {
    const item = finalData.find((d) => d.category === catLabel);
    if (!item) return "";
    const { score } = item;
    const rank = sorted.findIndex((d) => d.category === catLabel) + 1; // 1-based

    let rankText;
    // We'll define "top 2" as "high," "bottom 2" as "low," otherwise "moderate"
    if (rank === 1) {
      rankText = `This is your highest score at ${score}%. You likely have strong interests here.`;
    } else if (rank === 6) {
      rankText = `This is your lowest score at ${score}%. You may not find this area as appealing.`;
    } else if (rank <= 2) {
      rankText = `One of your higher scores at ${score}%. You may enjoy focusing on this area.`;
    } else if (rank >= 5) {
      rankText = `One of your lower scores at ${score}%. You might explore other areas first.`;
    } else {
      rankText = `This score is ${score}%, which ranks #${rank} out of 6. This indicates moderate interest.`;
    }
    return rankText;
  };

  return (
    <div style={{ width: "100%", maxWidth: 600, margin: "0 auto" }}>
      {/* Radar Chart Container */}
      <div style={{ width: "100%", height: 400 }}>
        <ResponsiveContainer>
          <RadarChart data={finalData} outerRadius="70%">
            <PolarGrid stroke="#ccc" strokeDasharray="3 3" />

            {/* Category Labels (clickable) */}
            <PolarAngleAxis
              dataKey="category"
              tick={(props) => (
                <CustomPolarAngleAxisTick
                  {...props}
                  setSelectedCategory={setSelectedCategory}
                />
              )}
            />

            {/* Hide numeric axis lines and labels */}
            <PolarRadiusAxis
              domain={[0, "dataMax + 10"]}
              tick={false}
              axisLine={false}
            />

            {/* Tooltip on hover -> shows e.g. "25%" */}
            <Tooltip formatter={(value) => `${value}%`} />

            {/* The radar shape with the original single animation */}
            <Radar
              name="RIASEC"
              dataKey="score"
              stroke="#8884d8"
              fill="#8884d8"
              fillOpacity={0.6}
              isAnimationActive={true}
              animationBegin={200}
              animationDuration={1500}
              animationEasing="ease-in-out"
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Click-to-show detail box */}
      <div
        className={`mt-4 p-3 rounded border border-gray-300 bg-gray-50 transition-all duration-300 ease-in-out ${
          selectedCategory ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {selectedCategory && (
          <>
            <p className="font-semibold mb-1">{selectedCategory}</p>
            <p className="text-sm mb-1">
              {descriptions[selectedCategory] || "No description available."}
            </p>
            <p className="text-sm italic">{getRelativeRankInfo(selectedCategory)}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default RIASECHexagonChart;
